import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useState } from "react";
import Dropdown from "../components/common/Dropdown";
import { PublicGridSkeleton } from "../components/common/PublicPageSkeletons";
import UniversityCard from "../components/common/UniversityCard";
import { universityService } from "../services/api/universityService";
import type { University } from "../types/university";

const SORT_OPTIONS = [
  { value: "name", label: "Name (A-Z)" },
  { value: "-name", label: "Name (Z-A)" },
  { value: "courses", label: "Most Programs" },
  { value: "-courses", label: "Least Programs" },
];

const ITEMS_PER_PAGE_OPTIONS = [
  { value: "12", label: "12 per page" },
  { value: "24", label: "24 per page" },
  { value: "36", label: "36 per page" },
];

const CITY_OPTIONS = [
  { value: "", label: "All Cities" },
  { value: "Seoul", label: "Seoul" },
  { value: "Busan", label: "Busan" },
  { value: "Daejeon", label: "Daejeon" },
  { value: "Yongin/Cheonan", label: "Yongin/Cheonan" },
  { value: "Pocheon", label: "Pocheon" },
  { value: "Gimhae", label: "Gimhae" },
  { value: "Jeonju", label: "Jeonju" },
  { value: "Yangsan", label: "Yangsan" },
];

const UniversityPage: React.FC = () => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [sortBy, setSortBy] = useState("courses");
  const [itemsPerPage, setItemsPerPage] = useState("12");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const fetchUniversities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};

      if (searchTerm) {
        filters.search = searchTerm;
      }

      if (selectedCity) {
        filters.city = selectedCity;
      }

      // Note: We'll handle sorting on the client side
      const response = await universityService.getAllUniversities(
        currentPage,
        parseInt(itemsPerPage),
        filters
      );

      if (response.data && response.data.success && response.data.data) {
        let fetchedUniversities = response.data.data.universities || [];

        // Apply client-side sorting
        if (sortBy) {
          fetchedUniversities = [...fetchedUniversities].sort((a, b) => {
            switch (sortBy) {
              case "name":
                return a.name.localeCompare(b.name);
              case "-name":
                return b.name.localeCompare(a.name);
              case "courses":
                return b._count.courses - a._count.courses; // Most programs first
              case "-courses":
                return a._count.courses - b._count.courses; // Least programs first
              default:
                return 0;
            }
          });
        }

        setUniversities(fetchedUniversities);
        setPagination(response.data.data.pagination || null);
      } else {
        setError(response.data?.message || "Failed to fetch universities");
      }
    } catch (err) {
      console.error("Error fetching universities:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, selectedCity, sortBy]);

  useEffect(() => {
    fetchUniversities();
  }, [fetchUniversities]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCity, sortBy, itemsPerPage]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCity("");
    setSortBy("name");
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const pages = [];
    const currentPageNum = pagination.page;
    const totalPages = pagination.pages;

    // Show first page
    if (currentPageNum > 3) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-2 text-sm text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors"
        >
          1
        </button>
      );
      if (currentPageNum > 4) {
        pages.push(
          <span key="dots1" className="px-3 py-2 text-gray-400">
            ...
          </span>
        );
      }
    }

    // Show pages around current page
    for (
      let i = Math.max(1, currentPageNum - 2);
      i <= Math.min(totalPages, currentPageNum + 2);
      i++
    ) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-2 text-sm rounded-md transition-colors ${i === currentPageNum
            ? "bg-cyan-600 text-white"
            : "text-gray-600 hover:text-cyan-600 hover:bg-cyan-50"
            }`}
        >
          {i}
        </button>
      );
    }

    // Show last page
    if (currentPageNum < totalPages - 2) {
      if (currentPageNum < totalPages - 3) {
        pages.push(
          <span key="dots2" className="px-3 py-2 text-gray-400">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-2 text-sm text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors"
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="flex justify-center items-center space-x-2 mt-12">
        <button
          onClick={() => handlePageChange(Math.max(1, currentPageNum - 1))}
          disabled={currentPageNum === 1}
          className="px-4 py-2 text-sm text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() =>
            handlePageChange(Math.min(totalPages, currentPageNum + 1))
          }
          disabled={currentPageNum === totalPages}
          className="px-4 py-2 text-sm text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Discover South Korean Universities
            </h1>
            <p className="text-xl text-cyan-100 mb-8">
              Explore world-class educational institutions offering innovative
              programs and cutting-edge research opportunities
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-300">
                  {pagination?.total || 0}
                </div>
                <div className="text-cyan-100">Universities</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-300">
                  {universities.reduce(
                    (acc, uni) => acc + uni._count.courses,
                    0
                  )}
                </div>
                <div className="text-cyan-100">Total Courses</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filters Section */}
      <section className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Search Bar */}
            <div className="relative mb-6">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search universities by name, city, or keyword..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-lg"
              />
            </div>

            {/* Filter Toggle for Mobile */}
            <div className="flex justify-between items-center mb-6 lg:hidden">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                <span>Filters</span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform ${showFilters ? "rotate-180" : ""
                    }`}
                />
              </button>
            </div>

            {/* Filters */}
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${showFilters ? "block" : "hidden lg:grid"
                }`}
            >
              <Dropdown
                label="City"
                options={CITY_OPTIONS}
                value={selectedCity}
                onChange={(value) => setSelectedCity(String(value))}
                placeholder="Select City"
              />

              <Dropdown
                label="Sort By"
                options={SORT_OPTIONS}
                value={sortBy}
                onChange={(value) => setSortBy(String(value))}
                placeholder="Sort By"
              />

              <Dropdown
                label="Items Per Page"
                options={ITEMS_PER_PAGE_OPTIONS}
                value={itemsPerPage}
                onChange={(value) => setItemsPerPage(String(value))}
                placeholder="Items Per Page"
              />

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-3 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Results Summary */}
            <div className="mt-6 flex justify-between items-center text-sm text-gray-600">
              <div>
                {pagination && (
                  <span>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} universities
                  </span>
                )}
              </div>
              {(searchTerm || selectedCity) && (
                <div className="flex flex-wrap gap-2">
                  {searchTerm && (
                    <span className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-xs">
                      Search: "{searchTerm}"
                    </span>
                  )}
                  {selectedCity && (
                    <span className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-xs">
                      City: {selectedCity}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Universities Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <PublicGridSkeleton count={12} variant="university" />
            ) : error ? (
              <div className="text-center py-12">
                <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">
                    Error Loading Universities
                  </h3>
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={fetchUniversities}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : universities.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 max-w-md mx-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    No Universities Found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Try adjusting your search criteria or filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {universities.map((university) => (
                    <UniversityCard
                      key={university.id}
                      university={university}
                    />
                  ))}
                </div>
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default UniversityPage;
